import { useEffect } from 'react';
import useTaskInboxStore from '../useTaskInboxStore';

const ACTIVE_POLL_MS = 15000;
const TERMINAL_STATUSES = new Set(['DONE', 'CANCELLED']);

function resolveEffectiveProgressStatus(liveStatus, fallbackStatus, hasFormInstance) {
  if (liveStatus) return liveStatus;
  if (fallbackStatus) return fallbackStatus;
  return hasFormInstance ? 'ACTIVE' : null;
}

/**
 * Keeps a chat tool card in sync with the persisted form/task instance. Subscribes to
 * {@code dynamicFormsById} (updated on draft save/submit in the drawer or task inbox) and
 * polls while the instance is still awaiting submission.
 */
export function useLiveFormProgress(formInstanceId, fallbackStatus = null) {
  const liveForm = useTaskInboxStore((state) =>
    formInstanceId ? state.dynamicFormsById[formInstanceId] : null
  );

  useEffect(() => {
    if (!formInstanceId) return undefined;

    let cancelled = false;
    let pollId = null;

    const fetchOnce = async () => {
      if (cancelled) return;
      await useTaskInboxStore.getState().fetchDynamicForm(formInstanceId);
    };

    const schedulePoll = () => {
      if (pollId != null) return;
      pollId = window.setInterval(() => {
        const current = useTaskInboxStore.getState().dynamicFormsById[formInstanceId];
        const status = current?.progressStatus;
        if (status && TERMINAL_STATUSES.has(status)) {
          window.clearInterval(pollId);
          pollId = null;
          return;
        }
        fetchOnce();
      }, ACTIVE_POLL_MS);
    };

    fetchOnce().then(() => {
      if (cancelled) return;
      const current = useTaskInboxStore.getState().dynamicFormsById[formInstanceId];
      const status = current?.progressStatus || fallbackStatus;
      if (!status || status === 'ACTIVE' || status === 'NOT_STARTED') {
        schedulePoll();
      }
    });

    return () => {
      cancelled = true;
      if (pollId != null) window.clearInterval(pollId);
    };
  }, [formInstanceId, fallbackStatus]);

  const progressStatus = resolveEffectiveProgressStatus(
    liveForm?.progressStatus,
    fallbackStatus,
    Boolean(formInstanceId)
  );

  return {
    progressStatus,
    liveForm,
    isSubmitted: progressStatus === 'DONE',
    isActive: progressStatus === 'ACTIVE' || progressStatus === 'NOT_STARTED',
    isCancelled: progressStatus === 'CANCELLED',
  };
}
